import { memo, useCallback, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useCloseModal } from '@/hooks/useCloseModal';
import { isDialogSubmitKey } from '@/util/events';

export type ConfirmDialogProps = {
  title: string;
  subtitle?: React.ReactNode;
  children?: React.ReactNode;
  primaryButtonText?: string;
  secondaryButtonText?: string;
  onConfirm?: (() => Promise<void>) | (() => void);
  onCancel?: () => void;
  danger?: boolean;
  hideCancel?: boolean;
  primaryDisabled?: boolean;
  preventClose?: boolean;
  /** External loading control; when undefined, internal loading state is used while onConfirm resolves. */
  isLoading?: boolean;
  /** Auto-close the modal after onConfirm resolves. Default: true. */
  closeOnConfirm?: boolean;
  /**
   * Fire the primary action on Enter, matching the legacy Modal's `autoFocus` behaviour.
   * Opt-in: most confirmations are destructive and must not be triggered by a stray Enter.
   */
  submitOnEnter?: boolean;
  /**
   * Open state, for dialogs mounted locally by their page. Omit it when rendering through
   * the Redux ModalConductor, which mounts the component only while the modal is open —
   * dismissal then falls back to popping the modal stack.
   */
  open?: boolean;
  /** Dismiss callback, required alongside `open`. */
  onOpenChange?: (open: boolean) => void;
};

const isPromise = (value: unknown): value is Promise<unknown> =>
  !!value && typeof (value as Promise<unknown>).then === 'function';

function ConfirmDialog({
  title,
  subtitle,
  children,
  primaryButtonText,
  secondaryButtonText,
  onConfirm,
  onCancel,
  danger = false,
  hideCancel = false,
  primaryDisabled = false,
  preventClose = false,
  isLoading: externalLoading,
  closeOnConfirm = true,
  submitOnEnter = false,
  open,
  onOpenChange,
}: ConfirmDialogProps) {
  const { t } = useTranslation();
  const closeModal = useCloseModal();
  const [internalLoading, setInternalLoading] = useState(false);
  const isLoading = externalLoading ?? internalLoading;
  const isControlled = open !== undefined;

  const close = useCallback(() => {
    if (isControlled) {
      onOpenChange?.(false);
    } else {
      closeModal();
    }
  }, [isControlled, onOpenChange, closeModal]);

  const handleClose = useCallback(() => {
    if (onCancel) {
      onCancel();
    } else {
      close();
    }
  }, [onCancel, close]);

  const handlePrimary = useCallback(async () => {
    if (!onConfirm) {
      close();
      return;
    }
    try {
      setInternalLoading(true);
      const result = onConfirm();
      if (isPromise(result)) {
        await result;
      }
      if (closeOnConfirm) {
        close();
      }
    } finally {
      setInternalLoading(false);
    }
  }, [onConfirm, closeOnConfirm, close]);

  return (
    <Dialog
      open={open ?? true}
      onOpenChange={(nextOpen) => !nextOpen && !preventClose && handleClose()}
    >
      <DialogContent
        showCloseButton={!preventClose}
        onPointerDownOutside={(event) => {
          if (preventClose) {
            event.preventDefault();
          }
        }}
        onEscapeKeyDown={(event) => {
          if (preventClose) {
            event.preventDefault();
          }
        }}
        onKeyDown={(event) => {
          if (!submitOnEnter || primaryDisabled || isLoading || !isDialogSubmitKey(event)) {
            return;
          }
          event.preventDefault();
          handlePrimary();
        }}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {subtitle && <DialogDescription>{subtitle}</DialogDescription>}
        </DialogHeader>
        {children}
        <DialogFooter>
          {!hideCancel && (
            <Button variant="ghost-neutral" onClick={handleClose}>
              {secondaryButtonText || t('common.cancel')}
            </Button>
          )}
          <Button
            variant={danger ? 'destructive' : 'primary'}
            onClick={handlePrimary}
            disabled={primaryDisabled}
            isLoading={isLoading}
          >
            {primaryButtonText || t('common.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default memo(ConfirmDialog);
